// Loan calculation helpers — mirror the Excel logic.

// Excel-style rounding (round half away from zero)
export function round2(n) {
  const x = Number(n) || 0;
  const sign = x < 0 ? -1 : 1;
  return (sign * Math.round(Math.abs(x) * 100 + 1e-9)) / 100;
}

// Simple / add-on interest:
//   total = principal + principal * monthly_rate * term
//   monthly = total / term
export function calcSimpleAmort(monthlyRate, termMonths, loanAmount) {
  const r = Number(monthlyRate) || 0;
  const n = Number(termMonths) || 0;
  const pv = Number(loanAmount) || 0;
  if (n <= 0) return 0;
  return round2((pv * (1 + r * n)) / n);
}

// PMT (compound) — annualRate is the annual rate; monthly period rate = annualRate / 12.
//   PMT = r * pv / (1 - (1+r)^-n)
export function calcPmtAmort(annualRate, termMonths, loanAmount) {
  const r = (Number(annualRate) || 0) / 12;
  const n = Number(termMonths) || 0;
  const pv = Number(loanAmount) || 0;
  if (n <= 0) return 0;
  if (r === 0) return round2(pv / n);
  return round2((r * pv) / (1 - Math.pow(1 + r, -n)));
}

// Look up annual rate by loan term from PMT rate table.
export function lookupAnnualRate(pmtRates, termMonths) {
  if (!pmtRates || pmtRates.length === 0) return 0;
  const t = Number(termMonths);
  const exact = pmtRates.find((r) => Number(r.term_months) === t);
  if (exact) return Number(exact.annual_rate);
  const sorted = [...pmtRates].sort(
    (a, b) => Math.abs(a.term_months - t) - Math.abs(b.term_months - t)
  );
  return Number(sorted[0].annual_rate);
}

// Convenience: picks formula and rate source for the simulator.
export function calcMonthlyAmortization({ formula, monthlyRate, annualRate, termMonths, loanAmount }) {
  if (formula === "pmt") return calcPmtAmort(annualRate, termMonths, loanAmount);
  return calcSimpleAmort(monthlyRate, termMonths, loanAmount);
}

// Number of days from date_granted to maturity (date_granted + term months).
export function daysBetween(grantDate, termMonths) {
  if (!grantDate || !termMonths) return 0;
  const start = new Date(grantDate);
  const end = new Date(start);
  end.setMonth(end.getMonth() + Number(termMonths));
  const ms = end.getTime() - start.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

// Compute DST based on rules + constants.
export function computeDst({ dstSettings, loanForBusiness, termMonths, loanAmount, numberOfDays }) {
  if (!dstSettings) return { amount: 0, ruleId: null };
  const { constants, rules } = dstSettings;
  const t = Number(termMonths) || 0;
  const amt = Number(loanAmount) || 0;
  const days = Number(numberOfDays) || 0;
  const termCondMatches = (cond) =>
    cond === "Less than 12 months" ? t < 12 : t >= 12;
  const amountCondMatches = (cond) => {
    if (cond === "Any amount") return true;
    if (cond === "Above 250,000.00") return amt > constants.threshold_amount;
    if (cond === "250,000.00 and Below") return amt <= constants.threshold_amount;
    return false;
  };
  const rule = rules.find(
    (r) =>
      r.loan_for_business === loanForBusiness &&
      termCondMatches(r.term_condition) &&
      amountCondMatches(r.amount_condition)
  );
  if (!rule) return { amount: 0, ruleId: null };
  let amount = 0;
  if (rule.formula_type === "short_term") {
    amount =
      ((amt / constants.short_term_divisor) * constants.short_term_factor) *
      (days / constants.short_term_days_basis);
  } else if (rule.formula_type === "long_term") {
    amount = amt * constants.long_term_rate;
  } else {
    amount = 0;
  }
  return { amount: round2(amount), ruleId: rule.id };
}

export function formatPhp(value) {
  const n = round2(value);
  return n.toLocaleString("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatPct(value) {
  return `${(Number(value || 0) * 100).toFixed(2)}%`;
}

// Format a rate FRACTION (e.g. 0.025) as a percent NUMBER string (no % sign),
// with a single clear rounding rule and no unnecessary rounding:
//   - rounds to `decimals` (default 2) using round-half-away-from-zero
//   - trims trailing zeros so whole rates stay clean (0.02 -> "2", not "2.00"
//     and never "3"), while fractional rates keep their real digits
//     (0.025 -> "2.5", 0.0175 -> "1.75").
export function formatRatePct(fraction, decimals = 2) {
  const pct = (Number(fraction) || 0) * 100;
  const f = Math.pow(10, decimals);
  const sign = pct < 0 ? -1 : 1;
  const rounded = (sign * Math.round(Math.abs(pct) * f + 1e-9)) / f;
  // Number(...) drops trailing zeros; toFixed caps float noise first.
  return String(Number(rounded.toFixed(decimals)));
}

export function formatDate(d) {
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
}

export function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

// Excel RATE: solve the periodic rate for an annuity (fv=0, type=0):
//   pv*(1+r)^n + pmt*((1+r)^n - 1)/r = 0
// Uses bracketing + bisection (robust for normal loan cash flows).
export function computeRate(nper, pmt, pv) {
  const n = Number(nper) || 0;
  if (n <= 0) return NaN;
  const f = (r) => {
    if (Math.abs(r) < 1e-12) return pv + pmt * n;
    const t = Math.pow(1 + r, n);
    return pv * t + (pmt * (t - 1)) / r;
  };
  let lo = 1e-9;
  let hi = 1.0;
  let flo = f(lo);
  let fhi = f(hi);
  let guard = 0;
  while (flo * fhi > 0 && hi < 1e6 && guard < 200) {
    hi *= 2;
    fhi = f(hi);
    guard += 1;
  }
  if (flo * fhi > 0) return NaN;
  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    const fm = f(mid);
    if (Math.abs(fm) < 1e-6 || hi - lo < 1e-12) return mid;
    if (flo * fm < 0) {
      hi = mid;
    } else {
      lo = mid;
      flo = fm;
    }
  }
  return (lo + hi) / 2;
}

// Effective Interest Rate (mirrors Excel B17: (1+RATE(n, pmt, pv))^n - 1).
// NOTE: Excel raises to the power of the loan TERM in months (B8), not a fixed 12.
// pmt = monthly amortization (outflow), pv = net proceeds of loan.
export function computeEIR({ termMonths, monthlyAmort, netProceeds }) {
  const n = Number(termMonths) || 0;
  const pv = Number(netProceeds) || 0;
  const pay = -Math.abs(Number(monthlyAmort) || 0);
  if (n <= 0 || pv <= 0 || pay === 0) return 0;
  const r = computeRate(n, pay, pv);
  if (!isFinite(r)) return 0;
  return Math.pow(1 + r, n) - 1;
}

// Total add-on interest charged over the full term (sum of payments - principal).
export function totalInterest({ monthlyAmort, termMonths, loanAmount }) {
  const total = Math.abs(Number(monthlyAmort) || 0) * (Number(termMonths) || 0);
  return round2(total - (Number(loanAmount) || 0));
}

// Declining-balance amortization schedule — mirrors the "Amortization_JL Final"
// Excel exactly:
//   monthlyRate = annualRate / 12                      (Excel B12 = B11/12)
//   amort/month = PMT(monthlyRate, term, loanAmount)   (Excel E, constant)
//   Int Bal     = beginningBalance * monthlyRate       (Excel D = B*B12)
//   Principal   = amort - Int Bal                       (|Excel C = D + E|)
//   nextBalance = beginningBalance - Principal          (Excel B_next = B + C)
// Values are kept full-precision; callers format for display. `principal` and
// `amort` are returned as POSITIVE magnitudes (Excel stores them negative);
// note Principal + Int Bal === Amort/Month every row.
export function buildAmortizationSchedule({ annualRate, termMonths, loanAmount, dateGranted }) {
  const rate = Number(annualRate) || 0;
  const n = Math.max(0, Math.round(Number(termMonths) || 0));
  const pv = Number(loanAmount) || 0;
  const mr = rate / 12;
  if (n <= 0 || pv <= 0) return { payment: 0, monthlyRate: mr, rows: [] };
  const payment = mr === 0 ? pv / n : (mr * pv) / (1 - Math.pow(1 + mr, -n));
  const start = dateGranted ? new Date(dateGranted) : new Date();
  const rows = [];
  let bal = pv;
  for (let i = 1; i <= n; i += 1) {
    const intBal = bal * mr;
    let principal = payment - intBal;
    // Guard the last row so the balance closes exactly at zero.
    if (i === n) principal = bal;
    const beginningBalance = bal;
    bal = bal - principal;
    rows.push({
      month: i,
      dueDate: addMonths(start, i),
      beginningBalance,
      principal,
      intBal,
      amort: payment,
      endingBalance: bal < 0 && bal > -0.005 ? 0 : bal,
    });
  }
  return { payment, monthlyRate: mr, rows };
}

